import React, { useState, useEffect } from "react";
import IconButton from "@mui/material/IconButton";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import AddIcon from "@mui/icons-material/Add";
import FertilizerMixModal from "./FertilizerMixModal";
import {
  Box,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Tooltip,
} from "@mui/material";
import { axiosInstance, buildLegacyHeaders } from "../../axiosFetchers";
import { useAuth } from "../../../Components/Auth/useAuth";
import { useReferenceData } from "../../../hooks/useReferenceData";
import { FOUR_R_FERTILIZER_APPLICATION_CREATOR } from "../../../Assets/contextFactories_V0";
import CropYearDatePicker from "../CropYearDatePicker";

export default function FertilizerApplication4R({
  cropYear,
  data,
  index,
  error,
  fourRAssessment,
  setFourRAssessment,
  onAssessmentChange,
}) {
  const [isFertMixOpen, setFertMixOpen] = React.useState(false);
  const [updatedMixes, setUpdatedMixes] = useState(false);
  const [fertilizerMixesArray, setFertilizerMixesArray] = useState([]);
  const { user } = useAuth();
  const { referenceData } = useReferenceData([
    "application_methods",
    "fertilizer_timing",
    "soil_states",
  ]);
  const rowLabel = `4R fertilizer application #${index + 1}`;
  const fieldIsMissing = (field) =>
    Boolean(error[field]) &&
    (data[field] === "" || data[field] === null || data[field] === undefined);
  const rateIsInvalid =
    Boolean(error.application_rate) &&
    (data.application_rate === "" ||
      !Number.isFinite(Number(data.application_rate)) ||
      Number(data.application_rate) <= 0);

  const handleSaveFertilizerMix = async (fertMixData) => {
    const response = await axiosInstance.post(
      process.env.REACT_APP_API_URL + `/assessments/fertilizer_mixes`,
      {
        ...fertMixData,
      },
      {
        headers: buildLegacyHeaders(user),
      }
    );

    handleUpdateFertilizerApplication("fertilizer_mix_id", response.data.id);
    setFertMixOpen(false);
    setUpdatedMixes((prev) => !prev);
  };

  useEffect(() => {
    async function fetchMixes() {
      const response = await axiosInstance.get(
        process.env.REACT_APP_API_URL + `/assessments/fertilizer_mixes`,
        {
          headers: buildLegacyHeaders(user),
        }
      );

      setFertilizerMixesArray(response.data);
    }

    fetchMixes();
  }, [updatedMixes, user]);

  function handleUpdateFertilizerApplication(property, value) {
    onAssessmentChange();
    setFourRAssessment((prevState) => ({
      ...prevState,
      fertilizer_applications: (prevState.fertilizer_applications || []).map(
        (application, rowIndex) =>
          rowIndex === index ? { ...application, [property]: value } : application
      ),
    }));
  }

  function handleFertApplicationDelete() {
    onAssessmentChange();
    setFourRAssessment((prevState) => {
      const fertilizerApplications = prevState.fertilizer_applications || [];

      return {
        ...prevState,
        fertilizer_applications:
          fertilizerApplications.length <= 1
            ? [FOUR_R_FERTILIZER_APPLICATION_CREATOR()]
            : fertilizerApplications.filter((_, rowIndex) => rowIndex !== index),
      };
    });
  }

  function handleMoveUpDown(direction) {
    const fertilizerApplications = fourRAssessment.fertilizer_applications || [];
    if (fertilizerApplications.length <= 1) return;
    if (direction < 0 && index === 0) return;
    if (direction > 0 && index === fertilizerApplications.length - 1)
      return;

    onAssessmentChange();
    setFourRAssessment((prevState) => {
      const nextApplications = [...(prevState.fertilizer_applications || [])];
      const tmp = nextApplications[index];
      nextApplications[index] = nextApplications[index + direction];
      nextApplications[index + direction] = tmp;

      return {
        ...prevState,
        fertilizer_applications: nextApplications,
      };
    });
  }

  const methods = referenceData.application_methods || [];
  const timings = referenceData.fertilizer_timing || [];
  const depths = [0, 0.5, 1, 2, 2.5, 3, 3.5];
  const soilStates = referenceData.soil_states || [];

  return (
    <Box
      id={`four-r-fertilizer-application-row-${index}`}
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "minmax(0, 1fr)",
          sm: "repeat(2, minmax(0, 1fr))",
          lg: "repeat(4, minmax(0, 1fr))",
        },
        gap: 1.5,
        padding: { xs: 1.5, sm: 2 },
        "& > *": {
          minWidth: 0,
        },
      }}
    >
      <Box sx={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <Box>
          <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>Application {index + 1}</Box>
          <Box component="span" sx={{ ml: 1, fontSize: 13, color: "text.secondary" }}>
            {data.application_date || data.application_method || "New product pass"}
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexShrink: 0 }}>
          <Tooltip title="Move application up">
            <span>
              <IconButton size="small" aria-label={`Move ${rowLabel} up`} disabled={index === 0} onClick={() => handleMoveUpDown(-1)}>
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Move application down">
            <span>
              <IconButton
                size="small"
                aria-label={`Move ${rowLabel} down`}
                disabled={index === (fourRAssessment.fertilizer_applications || []).length - 1}
                onClick={() => handleMoveUpDown(1)}
              >
                <ArrowDownwardIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Delete application">
            <IconButton size="small" aria-label={`Delete ${rowLabel}`} onClick={handleFertApplicationDelete}>
              <ClearIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box>
        <CropYearDatePicker
          cropYear={cropYear}
          label="Date"
          value={data.application_date}
          onChange={(value) =>
            handleUpdateFertilizerApplication("application_date", value)
          }
          legacyFormat
          textFieldProps={{
            fullWidth: true,
            error: fieldIsMissing("application_date"),
            inputProps: { "aria-label": `${rowLabel} date` },
          }}
        />
      </Box>

      <Box>
        <FormControl fullWidth size="small">
          <InputLabel>Method</InputLabel>
          <Select
            value={data.application_method}
            onChange={(e) =>
              handleUpdateFertilizerApplication(
                "application_method",
                e.target.value
              )
            }
            label="Method"
            error={fieldIsMissing("application_method")}
            inputProps={{ "aria-label": `${rowLabel} method` }}
          >
            {methods.map((method) => (
              <MenuItem key={method.value} value={method.value}>
                {method.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box>
        <FormControl fullWidth size="small">
          <InputLabel>Crop Timing</InputLabel>
          <Select
            value={data.fertilizer_timing}
            onChange={(e) =>
              handleUpdateFertilizerApplication(
                "fertilizer_timing",
                e.target.value
              )
            }
            label="Crop Timing"
            error={fieldIsMissing("fertilizer_timing")}
            inputProps={{ "aria-label": `${rowLabel} timing` }}
          >
            {timings.map((timing) => (
              <MenuItem key={timing.value} value={timing.value}>
                {timing.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box>
        <FormControl fullWidth size="small">
          <InputLabel>Depth (inches)</InputLabel>
          <Select
            value={data.incorporation_depth}
            onChange={(e) =>
              handleUpdateFertilizerApplication(
                "incorporation_depth",
                e.target.value
              )
            }
            label="Depth (inches)"
            inputProps={{ "aria-label": `${rowLabel} incorporation depth` }}
          >
            {depths.map((depth) => (
              <MenuItem key={depth} value={depth}>
                {depth}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box>
        <FormControl fullWidth size="small">
          <InputLabel>Soil State</InputLabel>
          <Select
            value={data.soil_state}
            onChange={(e) =>
              handleUpdateFertilizerApplication("soil_state", e.target.value)
            }
            label="Soil State"
            error={fieldIsMissing("soil_state")}
            inputProps={{ "aria-label": `${rowLabel} soil state` }}
          >
            {soilStates.map((state) => (
              <MenuItem key={state.value} value={state.value}>
                {state.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box>
        <FormControl fullWidth size="small">
          <InputLabel>Fertilizer Mix</InputLabel>
          <Select
            value={data.fertilizer_mix_id}
            onChange={(e) =>
              handleUpdateFertilizerApplication(
                "fertilizer_mix_id",
                e.target.value
              )
            }
            label="Fertilizer Mix"
            error={fieldIsMissing("fertilizer_mix_id")}
            inputProps={{ "aria-label": `${rowLabel} fertilizer mix` }}
          >
            {fertilizerMixesArray.map((mix) => (
              <MenuItem key={mix.id} value={mix.id}>
                {mix.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button startIcon={<AddIcon />} onClick={() => setFertMixOpen(true)} size="small">
          New mix
        </Button>
      </Box>

      <Box>
        <div className="flex flex-col gap-2 sm:flex-row">
          <TextField
            type="number"
            size="small"
            label="Rate"
            value={data.application_rate ?? ""}
            onChange={(e) =>
              handleUpdateFertilizerApplication("application_rate", e.target.value)
            }
            error={rateIsInvalid}
            helperText={rateIsInvalid ? "Enter a rate greater than zero" : "Mixture mass per acre or hectare; nutrient amounts come from its composition."}
            inputProps={{ min: 0, step: "any", "aria-label": `${rowLabel} application rate` }}
          />
          <FormControl size="small" sx={{ minWidth: 92 }}>
            <InputLabel>Unit</InputLabel>
            <Select
              value={data.rate_unit || "lb/ac"}
              label="Unit"
              onChange={(e) =>
                handleUpdateFertilizerApplication("rate_unit", e.target.value)
              }
              error={fieldIsMissing("rate_unit")}
              inputProps={{ "aria-label": `${rowLabel} rate unit` }}
            >
              <MenuItem value="lb/ac">lb/ac</MenuItem>
              <MenuItem value="kg/ha">kg/ha</MenuItem>
            </Select>
          </FormControl>
        </div>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center" }}>
        <FormControlLabel
          label="Nitrification inhibitor"
          control={
            <Switch
              checked={Boolean(data.has_nitrification_inhibitor)}
              onChange={(e) =>
                handleUpdateFertilizerApplication(
                  "has_nitrification_inhibitor",
                  e.target.checked
                )
              }
              inputProps={{ "aria-label": `${rowLabel} nitrification inhibitor` }}
            />
          }
        />
      </Box>

      <FertilizerMixModal
        open={isFertMixOpen}
        handleClose={setFertMixOpen}
        handleSave={handleSaveFertilizerMix}
      />
    </Box>
  );
}
